import axios from "axios";
import func, { mapToast, showToast, toastStatuses } from "./functions";
import { getStore } from "../AppContext";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";


// For common config
axios.defaults.headers.post["Content-Type"] = "application/json";

export const baseURL = process.env.REACT_APP_JAVA_SERVER_URL

export const mainAxios = new axios.create({
  baseURL,
  //mos vendosni ketu parametra!!!
});

export const setAuthToken = (token, header = "Authorization") => {
  if (token) {
    const value = header == "Refresh-Token" ? token : ("Bearer " + token)
    //applying token
    mainAxios.defaults.headers.common[header] = value;
  } else {
    //deleting the token from header
    delete mainAxios.defaults.headers.common[header];
  }
};

const getRequestHeader = (headers, header) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    return headers.get(header);
  }
  return headers[header] ?? headers?.common?.[header];
};

const setRequestHeader = (headers, header, value) => {
  if (!headers) return;
  if (typeof headers.set === "function") {
    headers.set(header, value);
    return;
  }
  headers[header] = value;
  if (headers.common) {
    headers.common[header] = value;
  }
};

const getAccessToken = async ({token})  => {
  const refreshToken = func.getCookie("refreshToken")

  let config = {
    headers: {
      "Refresh-Token": refreshToken,
      "Authorization":  token
    }
  }
  return axios.post(baseURL + "public/refresh-token", {}, config)
    .then(res => {
      if (res.status === 200 && res?.data?.token) {
        return res?.data?.token
      }
      return null
    })
    .catch(err => { return null })
}

// Single-flight guard: when many requests find the token expired at the same
// time (e.g. after inactivity, when auto-save + navigation + polling all fire),
// they must share ONE refresh call instead of each POSTing public/refresh-token.
// A burst of concurrent refreshes gets rate-limited (429) and the proxy's 429
// often lacks CORS headers, so the browser surfaces it as "Network Error".
let refreshPromise = null;
const refreshAccessTokenOnce = (token) => {
  if (!refreshPromise) {
    refreshPromise = getAccessToken({ token }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export const clearUser = ({ reloadWindow, setUser }) => {
  const { settings } = getStore();
  document.cookie =
    "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie =
    "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  setUser(null);
  settings?.[1]?.(null);
  setAuthToken(null);
  setAuthToken(null, "Refresh-Token");
  if (reloadWindow) window.location.reload()
}

export const toCompanyId = (company) => {
  const companyId = parseInt(company)
  return Number.isNaN(companyId) || companyId <= 0 ? null : companyId
}

export const needsLoginCompanyConfirmation = (fullUser) => {
  if (!fullUser || fullUser?.role?.id === 1) return false
  return (fullUser?.companiesRights ?? []).filter(company => company?.authProviderAllowed).length > 1
}

export const confirmLoginCompany = ({ company, token }) => {
  const companyId = toCompanyId(company)
  if (!companyId) return Promise.resolve(false)
  const accessToken = token ?? func.getCookie("token")
  if (!accessToken) return Promise.resolve(false)
  return axios.post(baseURL + "apiEndpoint/confirm-login-company", {
    company: companyId,
  }, {
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    }
  })
    .then(res => res?.data?.status === true)
    .catch(() => false)
}

export const logout = ({ setUser, request = false, reloadWindow = false }) => {
  if (request) {
    const token=func.getCookie("token")
    const refreshToken=func.getCookie("refreshToken")
    const { selectedCompany } = getStore();
    axios.post(baseURL+"public/logout",{ company: toCompanyId(selectedCompany?.[0]) },{
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        "Refresh-Token":refreshToken
      }
    })
  }
  clearUser({ reloadWindow, setUser })


}

// let sessionWindow=null;
export const sessionWindowsMilliseconds = 60000 * 60 //60 min

mainAxios.interceptors.request.use(
  async (request) => {
    let {
      user: [user, setUser],
      selectedCompany: [selectedCompany],
      forbidden: [forbidden, setForbidden],
      sessionWindow
    } = getStore();
    if (forbidden) setForbidden(false)
    if (user && selectedCompany && !request?.data?.noWrapperData) {
      if (request?.data && typeof request.data !== "string") {
        request.data.username = user.username;
        request.data.company = request.data.company
          ? request.data.company
          : selectedCompany;
        request.data.params = JSON.stringify(request.data.params);
      }
    }
    if(request?.data && request?.data?.noWrapperData){
      const {noWrapperData, ...data} = request.data
      request.data = data;
    }
    // console.log({ [(request?.data?.object ?? "") + "Request"]: request });

    const token = getRequestHeader(request?.headers, "Authorization")
    let decodedToken = null
    if (token) {
      const tokenExp = jwt_decode(token)?.exp
      decodedToken = func.isNumeric(tokenExp) ? tokenExp * 1000 : null;
    }
    const now = new Date()
    // const sessionWindowsMilliseconds = 10000 //10 sec

    if (token && sessionWindow?.current && (now.getTime() - sessionWindow.current.getTime() > sessionWindowsMilliseconds)) {
      sessionWindow.current=null
      throw new axios.Cancel('Session is Over due to inactivity');
    } else {
      if (token) {
        sessionWindow.current = now
      }
    }

    if (token && decodedToken && (decodedToken < now.getTime())) {
      let accessToken = await refreshAccessTokenOnce(token)
      if (accessToken) {
        setRequestHeader(request.headers, "Authorization", "Bearer " + accessToken);
        setAuthToken(accessToken)
        func.setCookie("token", accessToken)
      } else {
        logout({ setUser,reloadWindow:true})
        throw new axios.Cancel('Unauthorized');
      }
    }
    return request;

  },
  (error) => {
    console.log({ error })
    return Promise.reject(error);
  }
);
mainAxios.interceptors.response.use(
  (response) => {
    if (response.config.showSuccessToast && response?.data?.status === true) {
      const messageType = response.data.messageType || toastStatuses.SUCCESS;
      const message = response.data.message || "Action completed successfully";
      showToast(message, messageType);
    }
    if (response?.config?.showErrorToast !== false) {
      showError(response)
    }
    return response;
  },
  async (error) => {
    console.log({ error })
    const {
      forbidden: [forbidden, setForbidden]
    } = getStore();
    //console.log({interceptorsResponse: error})
    const {
      user: [, setUser],
    } = getStore();
    if (error.response?.status === 401 && !error.config?._retried) {
      error.config._retried = true;
      const failedToken = getRequestHeader(error.config?.headers, "Authorization");
      const currentToken = mainAxios.defaults.headers.common["Authorization"];
      if (currentToken && currentToken !== failedToken) {
        setRequestHeader(error.config.headers, "Authorization", currentToken);
        return mainAxios(error.config);
      }
      const accessToken = await refreshAccessTokenOnce(failedToken || currentToken);
      if (accessToken) {
        setRequestHeader(error.config.headers, "Authorization", "Bearer " + accessToken);
        setAuthToken(accessToken);
        func.setCookie("token", accessToken);
        return mainAxios(error.config);
      }
      document.cookie =
        "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      setUser(null);
      setAuthToken(null);
    } else if (error.response?.status === 401) {
      const failedToken = getRequestHeader(error.config?.headers, "Authorization");
      const currentToken = mainAxios.defaults.headers.common["Authorization"];
      if (!currentToken || currentToken === failedToken) {
        document.cookie =
          "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        setUser(null);
        setAuthToken(null);
      }
    } else if (error.response?.status === 423) {
      return error;
    } else if (error.response?.status === 403) {
      if (func.getCookie("token")) {
        setForbidden(true)
      } else {
        document.cookie =
          "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        setUser(null);
        setAuthToken(null);
      }
    } else {
      if(error?.config?.showErrorToast !== false){
        const hasErrorResponse = showError(error?.response)
        if(hasErrorResponse){
          return Promise.reject(error);
        }
      }
      let options = {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        containerId: "all_toast",
      };
      let message = error.toJSON ? error.toJSON().message : error.message;
      if (
        document.querySelector(
          ".swal-overlay--show-modal #upload_invoice_modal"
        )
      ) {
        options.containerId = "upload_invoice";
        options.autoClose = false;
      }
      if (error?.code != "ERR_CANCELED" && error?.config?.showErrorToast !== false) {
        toast.error(message, options);
      }
    }

    return Promise.reject(error);
  }
);



const showError = (response) => {
  if (response?.data?.status === false) {
    let options = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      progress: undefined,
      containerId: "all_toast",
    };
    
    let message =
      response.data.message +
      " " +
      (response.data.faultReason ? response.data.faultReason : "");
    
    console.log({ response });
    
    if (
      document.querySelector(
        ".swal-overlay--show-modal #upload_invoice_modal"
      )
    ) {
      options.containerId = "upload_invoice";
      options.autoClose = false;
      
      try {
        const { error } = response.data;
        if (error) {
          message =
            response.data.message +
            (error.errorCode ? "\nCode: " + error.errorCode : "") +
            (error.errorSource ? "\nSource: " + error.errorSource : "") +
            (error.errorDescription
              ? "\nDescription: " + error.errorDescription
              : "");
        }
      } catch (e) {
        console.log({ e });
      }
    }
    
    const messageType = response.data.messageType || "ERROR";
    const toastMethod = mapToast[messageType] || toast.error;
    toastMethod(message, options);
    
    return true;
  }
  return false;
}



export default mainAxios;
